import { createContext, useContext, useEffect, useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";

// Cores padrão Salvia
const SALVIA_DEFAULTS = {
  mode: "SALVIA" as "SALVIA" | "CHANNEL",
  primaryColor: "#002F59",
  secondaryColor: "#C0FF00",
  logoUrl: null as string | null,
  displayName: "Insight NR1",
};

export interface BrandingContext {
  mode: "SALVIA" | "CHANNEL";
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | null;
  displayName: string;
  /** Cor derivada para hover/active no sidebar (mais clara que primary) */
  sidebarHover: string;
  /** Cor de texto sobre o primaryColor (branco ou preto baseado no contraste) */
  textOnPrimary: string;
  /** Cor de texto sobre o secondaryColor */
  textOnSecondary: string;
  /** Indica se o branding já foi carregado */
  isLoaded: boolean;
}

const BrandContext = createContext<BrandingContext>({
  ...SALVIA_DEFAULTS,
  sidebarHover: "#1A4D7F",
  textOnPrimary: "#FFFFFF",
  textOnSecondary: "#002F59",
  isLoaded: false,
});

export function useBrand() {
  return useContext(BrandContext);
}

/**
 * Calcula luminância relativa de uma cor hex para determinar contraste
 */
function getLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Retorna cor de texto com melhor contraste sobre o fundo
 */
function getContrastText(bgHex: string): string {
  try {
    const lum = getLuminance(bgHex);
    return lum > 0.4 ? "#1A1A1A" : "#FFFFFF";
  } catch {
    return "#FFFFFF";
  }
}

/**
 * Gera cor de hover (lighten/darken do primary)
 */
function generateHoverColor(hex: string): string {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const lum = getLuminance(hex);

    // Se a cor é escura, clarear; se clara, escurecer
    const factor = lum < 0.3 ? 1.4 : 0.8;
    const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v * factor)));

    return `#${clamp(r).toString(16).padStart(2, "0")}${clamp(g).toString(16).padStart(2, "0")}${clamp(b).toString(16).padStart(2, "0")}`;
  } catch {
    return "#1A4D7F";
  }
}

/**
 * Aplica CSS variables no :root para que todo o app use as cores do branding
 */
function applyCSSVars(branding: BrandingContext) {
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", branding.primaryColor);
  root.style.setProperty("--brand-secondary", branding.secondaryColor);
  root.style.setProperty("--brand-sidebar-hover", branding.sidebarHover);
  root.style.setProperty("--brand-text-on-primary", branding.textOnPrimary);
  root.style.setProperty("--brand-text-on-secondary", branding.textOnSecondary);
  if (branding.logoUrl) {
    root.style.setProperty("--brand-logo-url", `url(${branding.logoUrl})`);
  } else {
    root.style.removeProperty("--brand-logo-url");
  }
}

/**
 * BrandProvider — consome branding do auth.me via useAuth() (fonte única de verdade).
 * NÃO faz query auth.me própria para evitar duplicação de chamadas de autenticação.
 */
export function BrandProvider({ children }: { children: React.ReactNode }) {
  // Reutilizar a query auth.me que já é feita pelo useAuth()
  // O React Query/tRPC deduplica queries com a mesma key, mas usar useAuth()
  // garante que ambos compartilham exatamente a mesma instância de dados.
  const { user } = useAuth();

  const brandingValue = useMemo<BrandingContext>(() => {
    const raw = (user as any)?.branding ?? SALVIA_DEFAULTS;

    const primaryColor = raw.primaryColor || SALVIA_DEFAULTS.primaryColor;
    const secondaryColor = raw.secondaryColor || SALVIA_DEFAULTS.secondaryColor;

    return {
      mode: raw.mode || "SALVIA",
      primaryColor,
      secondaryColor,
      logoUrl: raw.logoUrl || null,
      displayName: raw.displayName || SALVIA_DEFAULTS.displayName,
      sidebarHover: generateHoverColor(primaryColor),
      textOnPrimary: getContrastText(primaryColor),
      textOnSecondary: getContrastText(secondaryColor),
      isLoaded: !!user,
    };
  }, [user]);

  // Aplicar CSS vars sempre que o branding mudar
  useEffect(() => {
    applyCSSVars(brandingValue);
  }, [brandingValue]);

  return (
    <BrandContext.Provider value={brandingValue}>
      {children}
    </BrandContext.Provider>
  );
}
